<template>
  <div class="body application-details-container">
    <div class="details">
      <PreBuiltIdmConnectorConfig
        v-if="showPrebuiltConfig"
      />
      <GenericScimConnectorConfig
        v-else
        :showOauthConfigReloadModal="showOauthConfigReloadModal"
        @closeConfigReloadModal="closeReloadConfigDecisionModal"
      />
    </div>
  </div>
</template>

<script>
import { authenticationMethods } from 'client/util/Constants/ApplicationConstants';
import { mapActions, mapState } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import Constants, { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import GenericScimConnectorConfig from 'client/applications/scim/GenericScimConnectorConfig.vue';
import HardcodedScimSupportMixin from 'client/applications/mixins/HardcodedScimSupportMixin';
import PreBuiltIdmConnectorConfig from 'client/applications/scim/PreBuiltIdmConnectorConfig.vue';

const { error, success } = Constants;
const { application } = ItemTypeConsts;
const ApplicationModel = StoreModelMap[application];
const { OAUTH2 } = authenticationMethods;

export default {
  name: 'ApplicationIdmConfiguration',

  components: {
    PreBuiltIdmConnectorConfig,
    GenericScimConnectorConfig,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
    HardcodedScimSupportMixin,
  ],

  data() {
    return {
      isNew: false,
      showOauthConfigReloadModal: false,
    };
  },

  computed: {
    ...mapState(ApplicationModel, {
      authenticationMethod: 'authenticationMethod',
      isScimActive: 'isScimActive',
      item: 'item',
      pendingIdmNotifications: 'pendingIdmNotifications',
      isPrebuiltButCustomConfigured: 'isPrebuiltButCustomConfigured',
    }),

    showPrebuiltConfig() {
      const { name } = this.item;
      const isBetaFeatureActive = this.hasBetaFeature(name) && !this.isBetaflagActive(name);

      if (isBetaFeatureActive) return false;

      const isScimSupported = !!this.item.provision || this.hardcodedIsScimSupported(name);

      return !this.isPrebuiltButCustomConfigured && isScimSupported;
    },
  },

  mounted() {
    this.handlePendingNotifications();
  },

  methods: {
    ...mapActions(ApplicationModel, ['clearPendingIdmNotifications']),

    ...mapActions(notificationModule, [
      'triggerSuccessNotification',
      'triggerErrorNotification',
    ]),

    closeReloadConfigDecisionModal() {
      // Config should have been already set
      // If user wants to keep the changes,
      // we just need to close the modal
      this.showOauthConfigReloadModal = false;
    },

    handlePendingNotifications() {
      const notificationTypeMap = new Map([
        [success, this.triggerSuccessNotification],
        [error, this.triggerErrorNotification],
      ]);

      const isMethodOAuth2 = this.authenticationMethod === OAUTH2;

      /**
       * "Drain" passed notifications queue by calling them
       */
      const renderNotifications = notificationsMap => {
        const notificationsArr = Array.from(notificationsMap.values());
        notificationsArr.forEach(
          ({ type, notificationConfig }) => notificationTypeMap
            .get(type)(notificationConfig),
        );
      };

      const separateNotificationByType = () => {
        const notifications = {
          successes: new Map(),
          errors: new Map(),
        };

        // Separate success and error notifications
        let messageExists;
        this.pendingIdmNotifications.forEach(notification => {
          const {
            type,
            notificationConfig: { message },
          } = notification;
          const { successes, errors } = notifications;

          messageExists = successes.get(message) || errors.get(message);
          if (!messageExists) {
            if (type === error) {
              errors.set(message, notification);
            }

            if (type === success) {
              successes.set(message, notification);
            }
          }
        });

        return notifications;
      };

      const { errors, successes } = separateNotificationByType();

      if (errors.size > 0) {
        renderNotifications(errors);

        // Show config reload decision modal if necessary, applies only to Custom REST Oauth
        this.showOauthConfigReloadModal = isMethodOAuth2 && this.isScimActive;
      } else if (successes.size > 0) {
        renderNotifications(successes);
      }

      this.clearPendingIdmNotifications();
    },
  },
};
</script>

<style module>
@import '../../css/sections.css';

.panel_text {
  font-size: 11px;
}

.groups {
  margin-bottom: 22px;
}
</style>
